import { computed, inject, signal, Service } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { Api, ApiClient } from '@app/api';
import { GoalsBackendService } from '@core/providers/backend/goals-backend.service';
import { IamBackendService } from '@core/providers/backend/iam-backend.service';
import { IntercomService } from '@core/providers/backend/intercom.service';
import { PermissionsBackendService } from '@core/providers/backend/permissions-backend.service';
import { ContextService } from '@core/providers/context/context.service';
import { NavigationService } from '@core/providers/navigation/navigation.service';
import { UserService } from '@core/providers/user/user.service';
import { CONSTANTS } from '@shared/constants';
import { type ContextObject } from '@shared/interfaces/login-object';
import { NavModelService } from '../navigation/nav-model.service';
import {
  type SwitcherAccount,
  type SwitcherCurrentContext,
  type SwitcherVenue,
} from './context-switcher.model';

/** The backend rejects a shorter term: a single character matches almost every venue. */
export const MIN_VENUE_SEARCH_LENGTH = 2;

@Service()
export class ContextSwitcherService {
  private readonly api = inject(ApiClient);
  private readonly permissionsBackend = inject(PermissionsBackendService);
  private readonly iamBackendService = inject(IamBackendService);
  private readonly contextService = inject(ContextService);
  private readonly navigationService = inject(NavigationService);
  private readonly userService = inject(UserService);
  private readonly intercomService = inject(IntercomService);
  private readonly goalsBackendService = inject(GoalsBackendService);
  private readonly navModel = inject(NavModelService);
  private readonly router = inject(Router);

  private readonly switching = signal<SwitcherCurrentContext | null>(null);

  /** Destination of an in-flight switch, or null. Read by the header trigger. */
  readonly switchingTarget = this.switching.asReadonly();
  readonly isSwitching = computed(() => this.switching() !== null);

  /**
   * A failed lookup yields an empty list rather than an error, on every reader here: these feed
   * signals whose consumers would otherwise latch into a permanent error state, leaving the
   * panel unusable for the rest of the session. `PermissionsBackendService` caches successes
   * only and its `share()` resets on error, so the next open retries.
   */
  getAccounts$(): Observable<ReadonlyArray<SwitcherAccount>> {
    return this.permissionsBackend.getPermissions$().pipe(
      map(permissions =>
        permissions
          .map(entry => ({
            uuid: entry.accountUuid,
            name: entry.accountName ?? '',
            type: entry.questionSetName ?? '',
            canUse: entry.permissions?.ACCOUNT_USE === true,
          }))
          .sort((first, second) =>
            first.name.toLowerCase().localeCompare(second.name.toLowerCase())
          )
      ),
      catchError(() => of([]))
    );
  }

  /** Venues of a single account. `PermissionsBackendService` caches the underlying call. */
  getVenues$(account: SwitcherAccount): Observable<ReadonlyArray<SwitcherVenue>> {
    return this.permissionsBackend.getVenuePermissions$(account.uuid).pipe(
      map(permissions =>
        permissions.map(entry => ({
          uuid: entry.venueUuid,
          name: entry.venueName ?? '',
          accountUuid: account.uuid,
          accountName: account.name,
        }))
      ),
      catchError(() => of([]))
    );
  }

  /**
   * Venues matching `search` across every account the caller can reach. One request instead of
   * the per-account fan-out `getVenues$` would need; the backend bounds and orders the result.
   */
  searchVenues$(search: string): Observable<ReadonlyArray<SwitcherVenue>> {
    return this.api.get(Api.fbi.venues.permissionsList, { query: { search } }).pipe(
      map(rows =>
        rows.map(row => ({
          uuid: row.venueUuid,
          name: row.venueName,
          accountUuid: row.accountUuid,
          accountName: row.accountName,
        }))
      ),
      catchError(() => of([]))
    );
  }

  isTopLevelDashboardAllowed$(): Observable<boolean> {
    const userUuid = this.userService.getUserUuid();
    if (userUuid === null) {
      return of(false);
    }

    return this.iamBackendService
      .isAllowedToAccessTopLevelDashboard(userUuid)
      .pipe(catchError(() => of(false)));
  }

  /**
   * Starts a context switch and owns it through to completion.
   *
   * The subscription lives here, not in the caller: this service is root-provided, so the two
   * round trips survive the component that started them being destroyed. The search palette
   * closes itself the instant a result is picked, which would otherwise cancel the switch.
   *
   * `switchingTarget` is the single record of an in-flight switch, so the header trigger shows
   * progress no matter which surface began it.
   *
   * The ACCOUNT_USE check lives here rather than in the callers because the switch persists the
   * context server-side: letting an account through without it would store a context whose pages
   * all answer 403, and the user would land there again on their next login.
   */
  switchTo(target: SwitcherCurrentContext): void {
    const { uuid } = target;
    if (this.switching() || !uuid || target.kind === 'topLevelDashboard') {
      return;
    }

    this.switching.set(target);

    const contextType =
      target.kind === 'account' ? CONSTANTS.CONTEXT.ACCOUNT : CONSTANTS.CONTEXT.VENUE;

    // Captured before the switch, while the current context's nav tree is still the live one.
    const currentPage = this.navModel.activePageKey();

    this.isAllowedTarget$(target)
      .pipe(
        switchMap(isAllowed => {
          if (!isAllowed) {
            return of(undefined);
          }

          this.goalsBackendService.clearGoals();

          return this.switchContext(contextType, uuid, currentPage);
        })
      )
      .subscribe({
        next: () => this.switching.set(null),
        error: () => this.switching.set(null),
      });
  }

  /**
   * Venues are only ever offered from a permissions list, so they need no second check. An
   * account does: it is selectable in both surfaces, but is only a destination with ACCOUNT_USE.
   * Fails closed — a permissions lookup that errors resolves to "not allowed", since storing the
   * wrong context outlasts the session while a refused switch does not.
   */
  private isAllowedTarget$(target: SwitcherCurrentContext): Observable<boolean> {
    if (target.kind !== 'account') {
      return of(true);
    }

    return this.getAccounts$().pipe(
      take(1),
      map(accounts => accounts.find(account => account.uuid === target.uuid)?.canUse === true)
    );
  }

  goToTopLevelDashboard(): void {
    this.navigationService.goToTopLevelDashboard();
  }

  private switchContext(
    contextType: string,
    uuid: string,
    currentPage: string | null
  ): Observable<void> {
    const currentUserUuid = this.userService.getUserUuid();
    if (currentUserUuid === null) {
      return of(undefined);
    }

    this.intercomService.updateIntercomData({ user_id: currentUserUuid });

    return this.iamBackendService
      .updateCurrentContext(currentUserUuid, { uuid, type: contextType })
      .pipe(
        mergeMap(() => this.contextService.switchContext$(contextType, uuid)),
        tap(newContext =>
          this.navigateAfterSwitch(newContext, newContext.uuid ?? uuid, currentPage)
        ),
        map((): void => undefined)
      );
  }

  /**
   * Lands on the same page in the new context when it exists there, and on the context's own
   * landing page when it does not.
   */
  private navigateAfterSwitch(
    newContext: ContextObject,
    contextUuid: string,
    currentPage: string | null
  ): void {
    const isAccountContext = newContext.type === CONSTANTS.CONTEXT.ACCOUNT;
    const equivalent = currentPage
      ? this.navModel
          .destinationsFor({
            isAccountContext,
            contextUuid,
            canSeeSettings: isAccountContext
              ? newContext.isUserAccountAdmin === true
              : newContext.isUserVenueAdmin === true,
            // The new account's AI flag arrives on a later request, so CoPilot is never
            // treated as preservable and falls back to the landing page instead.
            isAiEnabled: false,
          })
          .find(destination => destination.pageKey === currentPage)
      : undefined;

    if (equivalent) {
      void this.router.navigateByUrl(equivalent.link);
      return;
    }

    if (isAccountContext) {
      this.navigationService.goToScoreboards(contextUuid);
      return;
    }

    this.navigationService.goToVenueMainScreen(contextUuid);
  }
}
